import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  BookOpen, 
  Search, 
  Plus, 
  Trash2, 
  Download, 
  Copy,
  Loader2 
} from 'lucide-react';
import { api } from '../../lib/api';

interface Skill {
  id: string;
  name: string;
  description: string;
  code: string;
  tags: string[];
  author: string;
  created_at: string;
  updated_at: string;
}

export function SkillsLibrary() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    try {
      const response = await api.get('/api/code-execution/skills');
      setSkills(response.data.skills || []);
    } catch (error) {
      console.error('Failed to load skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchSkills = async (query: string) => {
    if (!query.trim()) {
      await loadSkills();
      return;
    }

    try {
      const response = await api.get(`/api/code-execution/skills/search?q=${encodeURIComponent(query)}`);
      setSkills(response.data.skills || []);
    } catch (error) {
      console.error('Failed to search skills:', error);
    }
  };

  const handleSearch = () => {
    searchSkills(searchQuery);
  };

  const copySkillCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('Code copied to clipboard!');
  };

  const exportSkill = (skill: Skill) => {
    const dataStr = JSON.stringify(skill, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `skill-${skill.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const filteredSkills = searchQuery
    ? skills
    : skills;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Skills List */}
      <div className="lg:col-span-1">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
              Skills Library
            </h3>
          </div>

          <div className="mb-4 flex space-x-2">
            <Input
              placeholder="Search skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button size="sm" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredSkills.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No skills found. Create your first skill from the code editor!
              </p>
            ) : (
              filteredSkills.map((skill) => (
                <div
                  key={skill.id}
                  onClick={() => setSelectedSkill(skill)}
                  className={`p-3 border rounded-md cursor-pointer transition-colors ${
                    selectedSkill?.id === skill.id
                      ? 'bg-blue-50 border-blue-300'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-sm">{skill.name}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {skill.description}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {skill.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Skill Details */}
      <div className="lg:col-span-2">
        {selectedSkill ? (
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold">{selectedSkill.name}</h3>
                <p className="text-gray-600 mt-1">{selectedSkill.description}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <span>Author: {selectedSkill.author}</span>
                  <span>Created: {new Date(selectedSkill.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copySkillCode(selectedSkill.code)}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => exportSkill(selectedSkill)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {selectedSkill.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Code</h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                <code>{selectedSkill.code}</code>
              </pre>
            </div>
          </Card>
        ) : (
          <Card className="p-6 flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center text-gray-500">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>Select a skill to view details</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

